import type { FunnelResult } from "@ti/core";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ArrowRight, Clock, Filter, Loader2, LogIn, Target, Trash2, TrendingUp } from "lucide-react";
import { useMemo, useState } from "react";
import { PointsDelta } from "@/components/domain/analytics";
import { PageHeader, TimeAgo } from "@/components/domain/misc";
import { StatTile } from "@/components/domain/stat";
import { ErrorPanel, Page, PageSkeleton } from "@/components/layout/page";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader } from "@/components/ui/card";
import { Dialog, DialogClose, DialogContent } from "@/components/ui/dialog";
import { EmptyState, Meter } from "@/components/ui/misc";
import { SessionLink, SessionsLink } from "@/features/analytics-shared/links";
import { BreakdownTable, DailyConversionChart, FunnelChart, FunnelSummary } from "@/features/funnels/funnel-chart";
import { FunnelStatusBadge, WINDOW_LABEL, fmtSeconds } from "@/features/funnels/meta";
import { q, useDeleteFunnel } from "@/lib/api";
import { fmtInt, fmtPct } from "@/lib/format";

export const Route = createFileRoute("/_app/funnels/$funnelId")({
  component: FunnelPage,
});

function FunnelPage() {
  const { funnelId } = Route.useParams();
  const result = useQuery({ ...q.funnel(funnelId), refetchInterval: (query) => (query.state.data?.funnel.status === "backfilling" ? 3_000 : false) });
  const agents = useQuery(q.agents());
  const names = useMemo(() => new Map(agents.data?.map((a) => [a.id, a.name])), [agents.data]);

  if (result.error)
    return (
      <Page>
        <Breadcrumb name={funnelId} />
        <ErrorPanel error={result.error} reset={() => void result.refetch()} />
      </Page>
    );
  if (!result.data) return <PageSkeleton />;
  const r = result.data;
  const f = r.funnel;
  const last = r.steps.at(-1);
  const timeToConvert = r.steps.reduce((s, x) => s + (x.medianSecondsFromPrev ?? 0), 0);

  return (
    <Page>
      <PageHeader
        breadcrumb={<Breadcrumb name={f.name} />}
        title={f.name}
        description={
          <span className="flex flex-col gap-1">
            {f.description ? <span>{f.description}</span> : null}
            <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
              <FunnelStatusBadge funnel={f} />
              <span className="font-mono text-[12px] text-fg-2">{f.agentIds.map((id) => names.get(id) ?? id).join(", ")}</span>
              <span aria-hidden>·</span>
              <span>{WINDOW_LABEL[f.window]}</span>
              <span aria-hidden>·</span>
              <span>
                {f.owner} · created <TimeAgo iso={f.createdAt} className="text-fg-3" />
              </span>
            </span>
          </span>
        }
        actions={<DeleteFunnel id={f.id} name={f.name} />}
      />

      {f.status === "backfilling" ? (
        <Card className="mb-4 border-violet/40">
          <CardBody className="flex flex-wrap items-center gap-4 py-3">
            <Loader2 className="size-4 animate-spin text-violet-text motion-reduce:animate-none" aria-hidden />
            <div className="min-w-0 flex-1">
              <p className="text-[13px] font-medium text-fg">Backfilling the last 30 days · {fmtPct(f.backfillProgress)}</p>
              <p className="text-xs text-fg-3">Replaying the funnel over stored, redacted sessions. Numbers grow until it finishes; new sessions are matched live.</p>
            </div>
            <Meter value={f.backfillProgress} className="w-56" tone="var(--violet)" label={`Backfill progress ${fmtPct(f.backfillProgress)}`} />
          </CardBody>
        </Card>
      ) : null}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile label="Entered · 7d" icon={<LogIn />} value={fmtInt(r.steps[0]?.sessions ?? f.entered7d)} sub={r.steps[0]?.name} />
        <StatTile label="Converted · 7d" icon={<Target />} value={fmtInt(last?.sessions ?? 0)} sub={last?.name} />
        <StatTile label="Overall conversion" icon={<TrendingUp />} value={fmtPct(last?.conversionFromStart ?? f.conversion7d, 1)} sub={<><PointsDelta value={f.conversionDelta} /> vs prior 7d</>} />
        <StatTile label="Median time to convert" icon={<Clock />} value={fmtSeconds(timeToConvert || null)} sub="sum of median step gaps" />
      </div>

      <Card className="mt-3">
        <CardHeader title="Funnel" description={`${r.steps.length} steps · where sessions went on, where they stopped and why`} />
        {r.steps.length ? <FunnelSummary steps={r.steps} /> : null}
        <CardBody>{r.steps.length ? <FunnelChart steps={r.steps} /> : <EmptyState icon={<Filter />} title="No sessions yet" />}</CardBody>
      </Card>

      <section className="mt-5" aria-labelledby="drops-title">
        <h2 id="drops-title" className="mb-2 text-xs font-semibold uppercase tracking-wide text-fg-3">
          Where sessions stop, and why
        </h2>
        <div className="grid gap-3 lg:grid-cols-2">
          {r.steps.slice(0, -1).map((s, i) => (
            <DropCard key={s.stepId} result={r} index={i} />
          ))}
        </div>
      </section>

      <div className="mt-3 grid items-start gap-3 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]">
        <Card>
          <CardHeader title="Daily conversion" description="Converted ÷ entered, last 14 days" />
          <CardBody>{r.daily.length ? <DailyConversionChart daily={r.daily} /> : <p className="text-xs text-fg-3">No data yet.</p>}</CardBody>
        </Card>
        <div className="flex flex-col gap-3">
          <Card>
            <CardHeader title="By agent version" description="Conversion vs overall" />
            <BreakdownTable
              label="Version"
              overall={f.conversion7d}
              rows={r.byVersion.map((v) => ({
                key: `${v.agentId}:${v.version}`,
                label: (
                  <span className="font-mono text-[12px]">
                    {names.get(v.agentId) ?? v.agentId} <span className="text-fg-3">{v.version}</span>
                  </span>
                ),
                entered: v.entered,
                conversion: v.conversion,
              }))}
            />
          </Card>
          <Card>
            <CardHeader title="By segment" description="From identify() traits" />
            <BreakdownTable label="Segment" overall={f.conversion7d} rows={r.bySegment.map((s) => ({ key: s.segment, label: s.segment, entered: s.entered, conversion: s.conversion }))} />
          </Card>
        </div>
      </div>
    </Page>
  );
}

function Breadcrumb({ name }: { name: string }) {
  return (
    <nav aria-label="Breadcrumb">
      <Link to="/funnels" className="hover:text-fg">
        Funnels
      </Link>
      <span className="px-1.5">/</span>
      <span className="text-fg-2">{name}</span>
    </nav>
  );
}

function DropCard({ result, index }: { result: FunnelResult; index: number }) {
  const s = result.steps[index]!;
  const next = result.steps[index + 1];
  const dropped = next ? s.sessions - next.sessions : 0;
  return (
    <Card className="flex flex-col">
      <CardHeader
        title={
          <span className="flex min-w-0 items-center gap-1.5">
            <span className="truncate">{s.name}</span>
            <ArrowRight className="size-3.5 shrink-0 text-fg-3" aria-label="then" />
            <span className="truncate text-fg-2">{next?.name}</span>
          </span>
        }
        description={`${fmtInt(dropped)} sessions stopped after step ${index + 1} (${fmtPct(s.sessions ? dropped / s.sessions : 0)}): ${fmtInt(s.dropped.failed)} failed, ${fmtInt(s.dropped.succeeded)} ended well another way${s.dropped.unknown ? `, ${fmtInt(s.dropped.unknown)} not known yet` : ""}`}
      />
      {s.dropReasons.length ? (
        <ul className="flex flex-1 flex-col divide-y">
          {s.dropReasons.map((d) => (
            <li key={d.reason} className="px-4 py-2.5">
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-[13px] text-fg">
                  {d.reason}
                  {d.exit !== "failed" ? <span className="ml-1.5 text-[11px] text-fg-3">{d.exit === "succeeded" ? "ended well" : "not known yet"}</span> : null}
                </span>
                <span className="shrink-0 text-[13px] font-semibold tabular text-fg">{fmtPct(d.share)}</span>
              </div>
              <Meter value={d.share} className="mt-1.5" tone={d.exit === "failed" ? "var(--critical)" : "var(--neutral-status)"} label={`${d.reason}: ${fmtPct(d.share)} of drops`} />
              {d.sampleSessionIds.length ? (
                <div className="mt-1.5 flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-[11px] text-fg-3">
                  e.g.
                  {d.sampleSessionIds.map((id) => (
                    <SessionLink key={id} id={id} />
                  ))}
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="flex-1 px-4 py-4 text-xs text-fg-3">{result.funnel.status === "backfilling" ? "Reasons appear once enough dropped sessions are analyzed." : "No dropped sessions to summarize."}</p>
      )}
      <div className="border-t px-4 py-2">
        <SessionsLink filter={{ funnelId: result.funnel.id, droppedAfterStep: index }}>View sessions that dropped here</SessionsLink>
      </div>
    </Card>
  );
}

function DeleteFunnel({ id, name }: { id: string; name: string }) {
  const [open, setOpen] = useState(false);
  const del = useDeleteFunnel();
  const navigate = useNavigate();
  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
        <Trash2 /> Delete
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent title={`Delete “${name}”?`} description="The funnel and its step history are removed for everyone. Sessions aren't affected. This can't be undone.">
          <div className="flex justify-end gap-2">
            <DialogClose asChild>
              <Button variant="ghost">Cancel</Button>
            </DialogClose>
            <Button
              variant="destructive"
              disabled={del.isPending}
              onClick={() =>
                del.mutate(id, {
                  onSuccess: () => {
                    setOpen(false);
                    void navigate({ to: "/funnels", replace: true });
                  },
                })
              }
            >
              {del.isPending ? <Loader2 className="animate-spin" /> : <Trash2 />} Delete funnel
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
